import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CalendarIcon, Save, AlertTriangle, Clock, TrendingUp, Euro, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  formatNumber, CATEGORY_LABELS, TIME_POINTS, SALES_LABELS, SALES_POINTS,
} from "@/lib/utils";

const fmtD = (d) => {
  if (!d) return "–";
  const p = String(d).split("-");
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : d;
};
const fmtEuro = (v) => (v > 0 ? `${new Intl.NumberFormat("de-DE").format(v)} EUR` : "–");

const TIME_CATS = ["produktion", "administration", "recherche", "cold_calls", "ordnung", "gesundheit", "sein", "lernen"];
const SALES_KEYS = [
  "leads", "cold_calls_done", "direkt_aus_cold_calls",
  "kontaktdaten_gesendet",
  "erstgespraech_gefuehrt", "erstgespraech_gebucht",
  "angebot_angefordert", "angebot_gesendet", "follow_up",
  "zusage", "anzahlung", "teilzahlung_2", "restzahlung",
];

const emptyTime = () => Object.fromEntries(TIME_CATS.map((k) => [k, 0]));
const emptySales = () => Object.fromEntries(SALES_KEYS.map((k) => [k, 0]));

/** Parse German number: "1,5" and "1.5" both → 1.5 */
const parseDE = (val) => {
  if (typeof val === "number") return val;
  if (!val || val === "") return 0;
  return parseFloat(String(val).replace(",", ".")) || 0;
};

const selectAll = (e) => e.target.select();

const nextField = (e) => {
  if (e.key === "Enter") {
    e.preventDefault();
    const fields = Array.from(document.querySelectorAll("[data-field]"));
    const i = fields.indexOf(e.target);
    if (i >= 0 && i < fields.length - 1) fields[i + 1].focus();
  }
};

const fieldProps = { onFocus: selectAll, onKeyDown: nextField, "data-field": "true" };

const inputCls = "w-full h-11 bg-transparent border border-zinc-800/60 text-right font-mono text-sm text-white px-3 focus:outline-none focus:border-zinc-500 transition-colors";

export default function HeutePage() {
  const { entryDate } = useParams();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(() => {
    if (entryDate) return new Date(entryDate + "T00:00:00");
    return new Date();
  });
  const [timeEntries, setTimeEntries] = useState(emptyTime);
  const [salesEntries, setSalesEntries] = useState(emptySales);
  const [auftragswert, setAuftragswert] = useState(0);
  const [notes, setNotes] = useState("");
  const [existingId, setExistingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  // Auftrags-Reconciliation
  const [currentOrders, setCurrentOrders] = useState([]);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [assignDialog, setAssignDialog] = useState(null); // { need, choices: [] }
  const [resolveDialog, setResolveDialog] = useState(null); // { count, rows: {id:{checked,reason}} }

  const dateStr = useMemo(() => {
    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const d = String(selectedDate.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [selectedDate]);

  useEffect(() => {
    loadEntry(dateStr);
  }, [dateStr]);

  const loadEntry = async (ds) => {
    try {
      const res = await api.get("/entries", { params: { entry_date: ds } });
      if (res.data.length > 0) {
        const e = res.data[0];
        setTimeEntries(e.time_entries || emptyTime());
        setSalesEntries({ ...emptySales(), ...e.sales_entries });
        setAuftragswert(e.auftragswert || 0);
        setNotes(e.notes || "");
        setExistingId(e.id);
        loadOrders(e.id);
      } else {
        setTimeEntries(emptyTime());
        setSalesEntries(emptySales());
        setAuftragswert(0);
        setNotes("");
        setExistingId(null);
        setCurrentOrders([]);
      }
    } catch {
      setExistingId(null);
    }
    loadUnassigned();
  };

  const loadOrders = async (id) => {
    try {
      const r = await api.get(`/entries/${id}/orders`);
      setCurrentOrders(r.data || []);
    } catch { setCurrentOrders([]); }
  };

  const loadUnassigned = async () => {
    try {
      const r = await api.get("/orders", { params: { unassigned: true } });
      setUnassignedOrders(r.data || []);
    } catch { setUnassignedOrders([]); }
  };

  const preview = useMemo(() => {
    let timePts = 0;
    Object.entries(timeEntries).forEach(([k, h]) => {
      if (k === "lernen") return;
      timePts += parseDE(h) * (TIME_POINTS[k] || 0);
    });
    let salesPts = 0;
    Object.entries(salesEntries).forEach(([k, c]) => {
      salesPts += Math.round(parseDE(c)) * (SALES_POINTS[k] || 0);
    });
    const lernenH = parseDE(timeEntries.lernen);
    // Eff. Arbeitszeit = Produktion + Administration + Vertrieb + Cold Calls
    const effAz = Math.round(
      (parseDE(timeEntries.produktion) + parseDE(timeEntries.administration) +
       parseDE(timeEntries.recherche) + parseDE(timeEntries.cold_calls)) * 100
    ) / 100;
    // Gesamtzeit = alle Kategorien
    const gesamt = Math.round(
      Object.values(timeEntries).reduce((s, v) => s + parseDE(v), 0) * 100
    ) / 100;
    return {
      timePoints: Math.round(timePts * 10) / 10,
      salesPoints: salesPts,
      total: Math.round((timePts + salesPts) * 10) / 10,
      lernenHours: Math.round(lernenH * 100) / 100,
      effektiveAZ: effAz,
      gesamtzeit: gesamt,
    };
  }, [timeEntries, salesEntries]);

  // Validation
  const dccVal = Math.round(parseDE(salesEntries.direkt_aus_cold_calls));
  const ccVal = Math.round(parseDE(salesEntries.cold_calls_done));
  const egfVal = Math.round(parseDE(salesEntries.erstgespraech_gefuehrt));
  const dccErrors = [];
  if (dccVal > ccVal) dccErrors.push("Kann nicht mehr sein als Cold Calls durchgefuehrt");
  if (dccVal > egfVal) dccErrors.push("Kann nicht mehr sein als Erstgespraeche gefuehrt");

  const awVal = parseDE(auftragswert);
  const zusageVal = Math.round(parseDE(salesEntries.zusage));
  const awWarning = awVal > 0 && zusageVal === 0;

  const doSave = async (extra = {}) => {
    setSaving(true);
    const payload = {
      date: dateStr,
      time_entries: Object.fromEntries(
        Object.entries(timeEntries).map(([k, v]) => [k, parseDE(v)])
      ),
      sales_entries: Object.fromEntries(
        Object.entries(salesEntries).map(([k, v]) => [k, Math.round(parseDE(v))])
      ),
      effektive_arbeitszeit: preview.effektiveAZ,
      auftragswert: parseDE(auftragswert),
      notes,
      ...extra,
    };
    try {
      let savedId = existingId;
      if (existingId) {
        await api.put(`/entries/${existingId}`, payload);
        toast.success("Eintrag aktualisiert");
      } else {
        const res = await api.post("/entries", payload);
        savedId = res.data.id;
        setExistingId(savedId);
        toast.success("Eintrag gespeichert");
      }
      setAssignDialog(null);
      setResolveDialog(null);
      if (savedId) await loadOrders(savedId);
      await loadUnassigned();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (dccErrors.length > 0) {
      toast.error("Bitte Validierungsfehler korrigieren");
      return;
    }
    if (awVal < 0) {
      toast.error("Auftragswert darf nicht negativ sein");
      return;
    }
    const currentCount = currentOrders.length;
    const delta = zusageVal - currentCount;

    if (delta > 0 && unassignedOrders.length > 0) {
      // Zuordnungsdialog: neuen Auftrag anlegen oder bestehenden verknuepfen
      setAssignDialog({ need: delta, choices: Array(delta).fill("new") });
      return;
    }
    if (delta < 0) {
      // Aufloesungsdialog: welche Auftraege entfernen + Grund
      const rows = {};
      currentOrders.forEach((o) => { rows[o.id] = { checked: false, reason: "punktekorrektur" }; });
      setResolveDialog({ count: -delta, rows });
      return;
    }
    await doSave();
  };

  const confirmAssign = async () => {
    // Keine Mehrfachauswahl desselben bestehenden Auftrags
    const picked = assignDialog.choices.filter((c) => c !== "new");
    if (new Set(picked).size !== picked.length) {
      toast.error("Ein bestehender Auftrag kann nur einmal verknuepft werden");
      return;
    }
    await doSave({ order_assignments: assignDialog.choices });
  };

  const confirmResolve = async () => {
    const selected = Object.entries(resolveDialog.rows).filter(([, v]) => v.checked);
    if (selected.length !== resolveDialog.count) {
      toast.error(`Bitte genau ${resolveDialog.count} Auftrag/Aufträge auswählen`);
      return;
    }
    const order_removals = selected.map(([id, v]) => ({ order_id: id, reason: v.reason }));
    await doSave({ order_removals });
  };

  const setTime = (key, val) => setTimeEntries((p) => ({ ...p, [key]: val }));
  const setSale = (key, val) => setSalesEntries((p) => ({ ...p, [key]: val }));

  return (
    <div className="p-4 md:p-8 space-y-4 max-w-3xl mx-auto" data-testid="heute-page">
      {/* Header + Date */}
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
          {existingId ? "Bearbeiten" : "Tageseintrag"}
        </h1>
        <Popover open={calOpen} onOpenChange={setCalOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="shrink-0 h-11 px-4 bg-zinc-950/50 border-zinc-800 text-sm"
              data-testid="date-picker-btn"
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
              {selectedDate.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-zinc-950 border-zinc-800" align="end">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => { if (d) { setSelectedDate(d); setCalOpen(false); } }}
              data-testid="date-calendar"
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Live Summary */}
      <Card className="bg-card border-border sticky top-0 z-10" data-testid="live-summary">
        <CardContent className="p-3">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Zeit</p>
              <p className="font-mono text-lg text-sky-400">{formatNumber(preview.timePoints)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Vertrieb</p>
              <p className="font-mono text-lg text-emerald-400">{formatNumber(preview.salesPoints)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Gesamt</p>
              <p className="font-mono text-xl font-bold text-white">{formatNumber(preview.total)}</p>
            </div>
          </div>
          <div className="flex justify-center gap-4 mt-1 text-[10px] text-zinc-600">
            <span>Eff: {preview.effektiveAZ} h</span>
            <span>Lernen: {preview.lernenHours} h</span>
            <span>Ges: {preview.gesamtzeit} h</span>
          </div>
        </CardContent>
      </Card>

      {/* Zeitkategorien */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-3.5 w-3.5 text-zinc-600" />
          <span className="text-[11px] uppercase tracking-wider text-zinc-500">Zeiterfassung</span>
          <span className="text-[9px] text-zinc-700 ml-auto">Stunden</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {TIME_CATS.map((key) => (
            <div key={key} className="bg-zinc-950/50 border border-zinc-800/60 p-2.5">
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-xs text-zinc-400">{CATEGORY_LABELS[key]}</span>
                <span className="text-[9px] text-zinc-600 font-mono">
                  {TIME_POINTS[key] ? `${TIME_POINTS[key]}/h` : "\u2014"}
                </span>
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={timeEntries[key]}
                onChange={(e) => setTime(key, e.target.value)}
                className={inputCls}
                data-testid={`time-${key}`}
                {...fieldProps}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Arbeitszeit (auto-berechnet) */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-zinc-900/30 border border-zinc-800/40 p-2.5">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-xs text-zinc-500">Eff. Arbeitszeit</span>
            <span className="text-[8px] text-zinc-700">auto</span>
          </div>
          <div className="h-11 flex items-center justify-end" data-testid="effektive-arbeitszeit">
            <span className="font-mono text-base text-zinc-300">{preview.effektiveAZ} h</span>
          </div>
        </div>
        <div className="bg-zinc-900/30 border border-amber-900/20 p-2.5">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-xs text-zinc-500">Gesamtzeit</span>
            <span className="text-[8px] text-zinc-700">auto</span>
          </div>
          <div className="h-11 flex items-center justify-end">
            <span className="font-mono text-base text-amber-400">{preview.gesamtzeit} h</span>
          </div>
        </div>
      </div>

      <Separator className="bg-zinc-900/50" />

      {/* Vertrieb & Projekte */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="h-3.5 w-3.5 text-zinc-600" />
          <span className="text-[11px] uppercase tracking-wider text-zinc-500">Vertrieb & Projekte</span>
          <span className="text-[9px] text-zinc-700 ml-auto">Anzahl</span>
        </div>

        {dccErrors.length > 0 && (
          <div className="p-2.5 bg-red-950/30 border border-red-900/30 mb-2" data-testid="validation-errors">
            {dccErrors.map((err, i) => (
              <p key={i} className="text-xs text-red-400 flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />{err}
              </p>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {SALES_KEYS.map((key) => {
            const isDcc = key === "direkt_aus_cold_calls";
            const hasDccError = isDcc && dccErrors.length > 0;
            return (
              <div
                key={key}
                className={`bg-zinc-950/50 border p-2.5 ${
                  hasDccError ? "border-red-800/50" : isDcc ? "border-amber-900/20" : "border-zinc-800/60"
                }`}
              >
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-xs text-zinc-400 leading-tight">{SALES_LABELS[key]}</span>
                  <span className="text-[9px] text-zinc-600 font-mono shrink-0 ml-1">
                    {SALES_POINTS[key] > 0 ? `${SALES_POINTS[key]}P` : "\u2014"}
                  </span>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={salesEntries[key]}
                  onChange={(e) => setSale(key, e.target.value)}
                  className={`${inputCls} ${hasDccError ? "!border-red-800/50" : ""}`}
                  data-testid={`sales-${key}`}
                  {...fieldProps}
                />
                {isDcc && (
                  <p className="text-[8px] text-zinc-700 mt-1">Davon direkt aus Cold Calls</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Auftragswert */}
      <div className="bg-zinc-950/50 border border-emerald-900/30 p-3">
        <div className="flex items-center justify-between mb-1.5">
          <div>
            <span className="text-xs text-zinc-400">Auftragswert gewonnen heute</span>
            <p className="text-[9px] text-zinc-600">Echter Vertragswert in Euro</p>
          </div>
          <Euro className="h-4 w-4 text-zinc-700" />
        </div>
        <input
          type="text"
          inputMode="decimal"
          value={auftragswert}
          onChange={(e) => setAuftragswert(e.target.value)}
          className="w-full h-11 bg-transparent border border-zinc-800/60 text-right font-mono text-base text-white px-3 focus:outline-none focus:border-zinc-500 transition-colors"
          placeholder="0"
          data-testid="auftragswert-input"
          {...fieldProps}
        />
        {awWarning && (
          <p className="text-[10px] text-amber-400 mt-1.5 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Auftragswert ohne Zusage / Auftrag
          </p>
        )}
      </div>

      {/* Notizen */}
      <div>
        <span className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1.5 block">Tageskommentar</span>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optionale Notizen..."
          className="bg-zinc-950/50 border-zinc-800 min-h-[70px] text-sm"
          data-testid="notes-textarea"
        />
      </div>

      {/* Save */}
      <div className="sticky bottom-20 md:bottom-4 z-10">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-12 bg-white text-black hover:bg-zinc-200 font-medium text-sm"
          data-testid="save-entry-btn"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Speichern..." : existingId ? "Aktualisieren" : "Speichern"}
        </Button>
      </div>

      {/* Zuordnungsdialog bei Erhoehung der Zusagen */}
      <Dialog open={!!assignDialog} onOpenChange={(o) => { if (!o) setAssignDialog(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-base flex items-center gap-2">
              <Link2 className="h-4 w-4 text-emerald-400" /> Zusage(n) zuordnen
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Lege für jede neue Zusage einen neuen Auftrag an oder verknüpfe einen bereits
              vorhandenen (z.&nbsp;B. aus einem angenommenen Angebot), um Doppelzählung zu vermeiden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {assignDialog && Array.from({ length: assignDialog.need }).map((_, i) => (
              <div key={i} data-testid={`assign-row-${i}`}>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Neue Zusage {i + 1}</p>
                <select
                  value={assignDialog.choices[i]}
                  onChange={(e) => setAssignDialog((p) => {
                    const choices = [...p.choices]; choices[i] = e.target.value; return { ...p, choices };
                  })}
                  className="w-full h-10 bg-zinc-900 border border-zinc-800 text-xs text-zinc-200 px-2 focus:outline-none focus:border-zinc-600"
                  data-testid={`assign-select-${i}`}
                >
                  <option value="new">Neuen Auftrag anlegen</option>
                  {unassignedOrders.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.kunde || "Ohne Kunde"}{o.projekt ? ` / ${o.projekt}` : ""} · {fmtD(o.decision_date)} · {fmtEuro(o.auftragswert)}{o.offer_id ? " (Angebot)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" className="h-9 text-xs" onClick={() => setAssignDialog(null)} data-testid="assign-cancel">
              Abbrechen
            </Button>
            <Button className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={confirmAssign} disabled={saving} data-testid="assign-confirm">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aufloesungsdialog bei Verringerung der Zusagen */}
      <Dialog open={!!resolveDialog} onOpenChange={(o) => { if (!o) setResolveDialog(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="resolve-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" /> Zusagen verringern
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              {resolveDialog && `Du reduzierst von ${currentOrders.length} auf ${currentOrders.length - resolveDialog.count}. Wähle genau ${resolveDialog.count} Auftrag/Aufträge zum Entfernen und den Grund.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1 max-h-[50vh] overflow-y-auto">
            {resolveDialog && currentOrders.map((o) => {
              const row = resolveDialog.rows[o.id] || { checked: false, reason: "punktekorrektur" };
              return (
                <div key={o.id} className={`border p-2.5 ${row.checked ? "border-amber-700/50 bg-amber-950/10" : "border-zinc-800/50 bg-zinc-900/30"}`} data-testid={`resolve-row-${o.id.slice(0, 8)}`}>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={row.checked}
                      onChange={(e) => setResolveDialog((p) => ({
                        ...p, rows: { ...p.rows, [o.id]: { ...row, checked: e.target.checked } },
                      }))}
                      className="accent-amber-500 mt-0.5"
                      data-testid={`resolve-check-${o.id.slice(0, 8)}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-zinc-200 truncate">
                        {o.kunde || "Kunde noch nicht zugeordnet"}{o.projekt ? ` / ${o.projekt}` : ""}
                      </p>
                      <p className="text-[10px] text-zinc-400 font-mono">{fmtD(o.decision_date)} · {fmtEuro(o.auftragswert)}</p>
                    </div>
                    {o.offer_id && <span className="text-[9px] text-cyan-400 shrink-0">aus Angebot</span>}
                  </label>
                  {row.checked && (
                    <div className="mt-2 pl-6 space-y-1">
                      {[
                        { v: "punktekorrektur", l: "Punktekorrektur (Fehleingabe)" },
                        { v: "ruecknahme", l: "Annahmerücknahme (Kunde abgesprungen)" },
                      ].map((opt) => (
                        <label key={opt.v} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`reason-${o.id}`}
                            checked={row.reason === opt.v}
                            onChange={() => setResolveDialog((p) => ({
                              ...p, rows: { ...p.rows, [o.id]: { ...p.rows[o.id], reason: opt.v } },
                            }))}
                            className="accent-amber-500"
                            data-testid={`resolve-reason-${opt.v}-${o.id.slice(0, 8)}`}
                          />
                          <span className="text-[10px] text-zinc-400">{opt.l}</span>
                        </label>
                      ))}
                      {row.reason === "punktekorrektur" && (
                        <p className="text-[9px] text-zinc-500">Gewonnener Auftrag bleibt bestehen und zählt weiter; nur Punkte/Tageszuordnung dieses Tages werden angepasst.</p>
                      )}
                      {row.reason === "ruecknahme" && (
                        <p className="text-[9px] text-zinc-500">Auftrag wird storniert{o.offer_id ? "; verknüpftes Angebot wird wieder geöffnet" : ""}.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" className="h-9 text-xs" onClick={() => setResolveDialog(null)} data-testid="resolve-cancel">
              Abbrechen
            </Button>
            <Button className="h-9 text-xs bg-amber-600 hover:bg-amber-700" onClick={confirmResolve} disabled={saving} data-testid="resolve-confirm">
              Bestätigen &amp; Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
